import { ReactElement } from 'react';
import { IFragmentInsuranceFields } from 'types/contentful';
import CTA from '../Cta';

interface DoctorImageAndPageSummaryProps {
  paymentsAccepted: string[];
  insuranceProviders: IFragmentInsuranceFields[];
  insuranceDisclaimer?: string;
  insuranceSlug?: string;
  billingInquirySlug?: string;
}

export default function DoctorImageAndPageSummary({
  paymentsAccepted,
  insuranceProviders,
  insuranceDisclaimer,
  insuranceSlug,
  billingInquirySlug,
}: DoctorImageAndPageSummaryProps): ReactElement {
  const chunkSize = 9;
  const sortedInsurances = [...insuranceProviders].sort((a, b) =>
    (a.insurer || '').localeCompare(b.insurer || '', undefined, { sensitivity: 'base' })
  );
  const insuranceColumns: IFragmentInsuranceFields[][] = [];
  for (let i = 0; i < sortedInsurances.length; i += chunkSize) {
    insuranceColumns.push(sortedInsurances.slice(i, i + chunkSize));
  }

  if (!paymentsAccepted && !insuranceProviders) {
    return <></>;
  }

  return (
    <div
      id="insurance_and_payments"
      className="px-site-component bg-muted py-10 md:py-20"
      style={{ scrollMarginTop: '13rem' }}
    >
      <div className="max-w-screen-max mx-auto">
        <div className="flex items-center mb-8">
          <h5 className="bordered-heading text-new-34 lg:text-4xl lg:leading-new-43 font-light text-primary-actual">
            Insurance & Payment
          </h5>
        </div>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          {paymentsAccepted && paymentsAccepted.length > 0 && (
            <div>
              <h2 className="text-xl md:text-2xl text-primary-actual font-heading mb-5">
                Payments Accepted
              </h2>
              <ul className="list-none text-lg text-primary-actual space-y-2">
                <li>{paymentsAccepted.join(', ')}</li>
              </ul>
            </div>
          )}

          {insuranceProviders && insuranceProviders.length > 0 && (
            <div>
              <h2 className="text-xl md:text-2xl text-primary-actual font-heading mb-5">
                Insurance Accepted
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1">
                {insuranceColumns.map((col, idx) => (
                  <ul key={idx} className="list-none text-lg text-primary-actual space-y-2">
                    {col.map((insurance, insuranceIdx) => (
                      <li key={insurance.insurer + insuranceIdx}>{insurance.insurer}</li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          )}
        </div>

        {insuranceProviders && insuranceProviders.length > 0 && insuranceDisclaimer && (
          <div className="mt-10 text-center max-w-screen-lg mx-auto">
            <span className="text-lg text-primary-actual">{insuranceDisclaimer}</span>
          </div>
        )}

        <div className="flex flex-col gap-5 md:gap-4 justify-center items-center md:flex-row md:w-full mx-auto mt-8">
          {insuranceSlug && (
            <div className="w-full md:w-auto max-w-300">
              <CTA
                text="Insurance & Payment"
                colorScheme="OutlinedTeal"
                slug={insuranceSlug}
                classNames="w-full"
                replaceBaseClasses={['md:w-auto']}
              />
            </div>
          )}

          {billingInquirySlug && (
            <div className="w-full md:w-auto max-w-300">
              <CTA
                text="Billing Inquiry"
                colorScheme="OutlinedTeal"
                slug={billingInquirySlug}
                classNames="w-full"
                replaceBaseClasses={['md:w-auto']}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
